<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableCustomField
    from "../../../../portal-editable-fields/custom-fields/EditableCustomField.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();
import {camelCase} from "../../../../../../helpers/common";

const handleUsernameBinding = (value) => {
    portalContentAndDesignStore.signupFormData.username = value;
};
</script>

<template>
    <div v-if="portalContentAndDesignStore.signupPageSettings?.signup_form?.form_has_fields" class="space-y-5">
        <template v-for="field in portalContentAndDesignStore.signupPageSettings?.signup_form?.fields">
            <div v-if="field.field_options?.portal_signup_form || field.field_options?.portal_signup_popup_form" :key="field.id">
                <!-- Built-in fields (name, last_name, email, password) -->
                <template v-if="field.field_options.field === 'name'">
                    <editable-text-field
                        v-model="field.title"
                        :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                        for-attribute="name"
                        text-type="label"
                    />
                    <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        v-model="portalContentAndDesignStore.signupFormData.name"
                        :class="portalContentAndDesignStore.getInputStyle"
                    />
                </template>
                <template v-else-if="field.field_options.field === 'last_name'">
                    <editable-text-field
                        v-model="field.title"
                        :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                        for-attribute="last_name"
                        text-type="label"
                    />
                    <input
                        type="text"
                        name="last_name"
                        id="last_name"
                        required
                        v-model="portalContentAndDesignStore.signupFormData.last_name"
                        :class="portalContentAndDesignStore.getInputStyle"
                    />
                </template>
                <template v-else-if="field.field_options.field === 'username'">
                    <editable-text-field
                        v-model="field.title"
                        :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                        for-attribute="email"
                        text-type="label"
                    />
                    <input
                        type="email"
                        name="email"
                        id="email"
                        required
                        v-model="portalContentAndDesignStore.signupFormData.email"
                        @input="handleUsernameBinding($event.target.value)"
                        :class="portalContentAndDesignStore.getInputStyle"
                    />
                </template>
                <template v-else-if="field.field_options.field === 'password'">
                    <editable-text-field
                        v-model="field.title"
                        :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                        for-attribute="password"
                        text-type="label"
                    />

                    <password
                        v-slot="{ childScope }"
                        :min="portalContentAndDesignStore.signupPageSettings.signup.password_validation_rules.min"
                        :regex="portalContentAndDesignStore.signupPageSettings.signup.password_validation_rules.regex"
                        :errors="false"
                        v-model="portalContentAndDesignStore.signupFormData.password"
                        v-cloak
                    >
                        <div>
                            <input
                                v-model="childScope.password"
                                type="password"
                                name="password"
                                field-id="password"
                                :class="portalContentAndDesignStore.getInputStyle"
                            />

                            <div class="grid grid-cols-2 gap-2 mt-5">
                                <template v-for="(text, regex) in portalContentAndDesignStore.signupPageSettings.signup.password_validation_rules.regex" :key="regex">
                                    <div class="flex items-center">
                                        <div
                                            class="h-2 w-2 rounded-full mr-2"
                                            :class="[childScope.rules[camelCase(text)] ? 'bg-black' : 'bg-gray-400']"
                                        ></div>
                                        <editable-text-field
                                            v-if="regex === '[a-z]'"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_lowercase_character"
                                            :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            text-type="span"
                                        />
                                        <editable-text-field
                                            v-else-if="regex === '[0-9]'"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_number"
                                            :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            text-type="span"
                                        />
                                        <editable-text-field
                                            v-else-if="regex === '[A-Z]'"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_uppercase_character"
                                            :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            text-type="span"
                                        />
                                    </div>
                                </template>

                                <div class="flex items-center">
                                    <div
                                        class="h-2 w-2 rounded-full mr-2"
                                        :class="[childScope.rules.passwordLength ? 'bg-black' : 'bg-gray-400']"
                                    ></div>
                                    <editable-text-field
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_characters_minimum"
                                        :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                        text-type="span"
                                    />
                                </div>
                            </div>
                        </div>
                    </password>
                </template>
            </div>

            <!-- Additional dynamic fields -->
            <div v-else>
                <editable-text-field
                    v-model="field.title"
                    text-type="label"
                    :for-attribute="`cfield_${field.id}`"
                    :class="['block text-sm mb-2 opacity-80', portalContentAndDesignStore.getPageContentTextColorClass]"
                />
                <editable-custom-field
                    :data="field"
                    v-model="portalContentAndDesignStore.signupFormData"
                />
            </div>
        </template>
    </div>
    <div v-else class="mb-5 space-y-5">
        <!-- Name field -->
        <div>
            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_name"
                :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                for-attribute="name"
                text-type="label"
            />
            <input
                type="text"
                name="name"
                id="name"
                required
                v-model="portalContentAndDesignStore.signupFormData.name"
                :class="portalContentAndDesignStore.getInputStyle"
            />
        </div>

        <!-- Email field -->
        <div>
            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_email"
                :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                for-attribute="email"
                text-type="label"
            />
            <input
                type="email"
                name="email"
                id="email"
                required
                v-model="portalContentAndDesignStore.signupFormData.email"
                @input="handleUsernameBinding($event.target.value)"
                :class="portalContentAndDesignStore.getInputStyle"
            />
        </div>

        <!-- Password Field with Rules -->
        <div>
            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_password"
                :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                for-attribute="password"
                text-type="label"
            />

            <password
                v-slot="{ childScope }"
                :min="portalContentAndDesignStore.signupPageSettings.signup.password_validation_rules.min"
                :regex="portalContentAndDesignStore.signupPageSettings.signup.password_validation_rules.regex"
                :errors="false"
                v-model="portalContentAndDesignStore.signupFormData.password"
                v-cloak
            >
                <div>
                    <input
                        v-model="childScope.password"
                        type="password"
                        name="password"
                        field-id="password"
                        :class="portalContentAndDesignStore.getInputStyle"
                    />

                    <div class="grid grid-cols-2 gap-2 mt-5">
                        <template v-for="(text, regex) in portalContentAndDesignStore.signupPageSettings.signup.password_validation_rules.regex" :key="regex">
                            <div class="flex items-center">
                                <div
                                    class="h-2 w-2 rounded-full mr-2"
                                    :class="[childScope.rules[camelCase(text)] ? 'bg-black' : 'bg-gray-400']"
                                ></div>
                                <editable-text-field
                                    v-if="regex === '[a-z]'"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_lowercase_character"
                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                    text-type="span"
                                />
                                <editable-text-field
                                    v-else-if="regex === '[0-9]'"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_number"
                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                    text-type="span"
                                />
                                <editable-text-field
                                    v-else-if="regex === '[A-Z]'"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_uppercase_character"
                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                    text-type="span"
                                />
                            </div>
                        </template>

                        <div class="flex items-center">
                            <div
                                class="h-2 w-2 rounded-full mr-2"
                                :class="[childScope.rules.passwordLength ? 'bg-black' : 'bg-gray-400']"
                            ></div>
                            <editable-text-field
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_characters_minimum"
                                :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                text-type="span"
                            />
                        </div>
                    </div>
                </div>
            </password>
        </div>
    </div>
</template>
